import { SearchModes } from '@/domainLogic/index.ts';
import { getSearchModeFromSearchParams } from '@/app/components/search/utils/query2.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { ADULTS_DEFAULT, URLParameters } from '@/app/common/urlParameters.ts';
import { SEARCH_TYPE, type SearchInfoData, type SearchInfoDetailData } from '@/app/search/types.ts';
import type { DefaultDestItems } from '@/app/components/search/utils';
import { getTotalNumAdults, getTotalNumChildren, getTotalNumInfants, getTotalNumRooms } from '@/utils/peopleUtils.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { getSearchParam } from '@/app/common/urlParametersUtils.ts';
import { differenceInCalendarDays } from 'date-fns';
import { isValidDate } from '@/app/components/search/Utilities.ts';
import { createDatesParams } from '@/app/common/memsearch/getSearchParams.ts';
import type { Localization } from '../common/Localization';
import getSearchParams from '../common/getSearchParams';
import {
  AnalyticsEventType,
  createPageCategory,
  createUserDeviceType,
  createUserDqData,
  getDuration,
  getOriginData,
  getSource,
  registerEvents,
} from './utils';
import getDestinationData from './utils/getDestinationData';
import logDevOnly from '../utils/logDevOnly';
import convertDateFrom from './utils/convertDateFrom';
import type { DefaultValues } from '../common/getDefaultValues';
import convertDateTo from './utils/convertDateTo';
import type { DealCard } from '../utils/buildDealCards/dealCardTypes';
import { createCommonPageParams } from './utils/platformData';

const categoryBySearchMode: { [key in SearchModes]: string } = {
  [SearchModes.HO]: 'hotel',
  [SearchModes.DP]: 'flightHotel',
  [SearchModes.TH]: 'trainHotel',
  [SearchModes.FF]: 'flightHotel', // Use default DP value for now as Flight Only isn't currently implemented
};

const countOffers = (dealCards: DealCard[]) =>
  dealCards
    ? {
        myPrice: dealCards?.filter((dealCard) => dealCard.product.rate.flags.myPrice).length,
        flashSale: dealCards?.filter((dealCard) => dealCard.product.promotions.flashSale).length,
      }
    : {};

// URL from CSW-111: &prefilter_checkin_checkout=yyyy-mm-dd,yyyy-mm-dd
// N.B. There are 3 names for the same thing!
//      1) The parameter name is "prefilter_checkin_checkout"
//      2) TSX calls it CUSTOM_CHECKIN_CHECKOUT
//      3) Platform Data uses "partialStay"
const createPartialStayData = (searchParams: SearchParams) => {
  const partialStayValue = searchParams?.[URLParameters.CUSTOM_CHECKIN_CHECKOUT];
  const hasPartialStay = partialStayValue && partialStayValue.indexOf(',') > -1;
  if (hasPartialStay) {
    try {
      const checkInDate = partialStayValue?.split(',')[0];
      const checkOutDate = partialStayValue?.split(',')[1];
      const validDates = isValidDate(new Date(checkInDate)) && isValidDate(new Date(checkOutDate));

      if (validDates) {
        const partialStayCheckInDate = checkInDate.replaceAll('-', '');
        const partialStayCheckOutDate = checkOutDate.replaceAll('-', '');
        const partialStayDuration = differenceInCalendarDays(new Date(checkOutDate), new Date(checkInDate));
        return {
          partialStay: 1,
          partialStayCheckInDate,
          partialStayCheckOutDate,
          partialStayDuration,
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // Drop through to default value
    }
  }

  return {
    partialStay: 0,
  };
};

type AnalyticsResultsEvent = {
  localization: Localization;
  totalNumberOfResults: number | null;
  searchId: string;
  defaultDestItems: DefaultDestItems[];
  defaultValues?: DefaultValues;
  isSearch: boolean;
  defaultSearch?: SearchParams;
  dealCards?: DealCard[];
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  commonData: CommonData;
};

export const analyticsResultsEvent = async ({
  localization,
  localization: { currency, businessProfile },
  totalNumberOfResults: totalNumberOfResultsProp,
  searchId,
  defaultDestItems,
  isSearch,
  defaultSearch,
  dealCards,
  searchInfoData,
  searchInfoDetailData,
  commonData,
}: AnalyticsResultsEvent) => {
  const totalNumberOfResults = totalNumberOfResultsProp || 0;
  const commonPageParams = createCommonPageParams(localization);
  const { userUid, sessionId, deviceType: fallbackDeviceType } = commonData;
  const { isBot } = commonData;
  const searchParams = getSearchParams({ exclude: ['page'] });

  let searchParamsToUse = defaultSearch;
  if (!searchParamsToUse) {
    const adddays = getSearchParam(searchParams, URLParameters.ADD_DAYS);
    const origin = searchParams[URLParameters.ORIGIN];
    const dateFromQuery = searchParams[URLParameters.DATE_FROM];
    const dateToQuery = searchParams[URLParameters.DATE_TO];
    const adults = searchParams[URLParameters.ADULTS] || `${ADULTS_DEFAULT}`;
    const ages = searchParams[URLParameters.AGES];
    const searchModeQuery = searchParams[URLParameters.SEARCH_MODE];
    const datesParams = createDatesParams(parseInt(adddays || '0', 10) || 0, dateFromQuery, dateToQuery);
    searchParamsToUse = {
      [URLParameters.DATE_FROM]: datesParams.datefrom,
      [URLParameters.DATE_TO]: datesParams.dateto,
      [URLParameters.ADULTS]: adults,
      [URLParameters.AGES]: ages,
      [URLParameters.ORIGIN]: origin,
      [URLParameters.SEARCH_MODE]: searchModeQuery,
    };
  }
  const dateFrom = searchParamsToUse[URLParameters.DATE_FROM];
  const dateTo = searchParamsToUse[URLParameters.DATE_TO];
  const adults = searchParamsToUse[URLParameters.ADULTS];
  const ages = searchParamsToUse[URLParameters.AGES];
  const origin = searchParamsToUse[URLParameters.ORIGIN];
  const searchMode = getSearchModeFromSearchParams(searchParams);

  if (searchParams[URLParameters.DESTINATION]) {
    const destinationData = await getDestinationData({
      destination: searchParams[URLParameters.DESTINATION],
      defaultDestItems,
      isSearch,
      searchMode,
      searchInfoDetailData,
    });
    const originData = origin ? await getOriginData({ origin, searchInfoDetailData }) : {};
    const duration = getDuration({ dateFrom, dateTo });
    const quantity = getTotalNumRooms(adults);
    const category = createPageCategory(searchParams);
    const productCategory = categoryBySearchMode[searchMode] || 'flightHotel';
    const departureDate = dateFrom === 'A' ? 'Any' : convertDateFrom(dateFrom);
    const sectionWhenResults =
      searchInfoData.searchType === SEARCH_TYPE.DESTINATIONS
        ? AnalyticsEventType.destinationsResults
        : AnalyticsEventType.results;
    const section = totalNumberOfResults === 0 ? AnalyticsEventType.noResults : sectionWhenResults;
    const totalNumAdults = getTotalNumAdults(adults || '');
    const totalNumChildren = getTotalNumChildren(ages || '');
    const totalNumInfants = getTotalNumInfants(ages || '');
    const numberOfGuests = totalNumAdults + totalNumChildren + totalNumInfants;
    const returnDate = convertDateTo(dateFrom, dateTo, departureDate);
    const userDqData = createUserDqData(commonData);
    const deviceType = createUserDeviceType(fallbackDeviceType, commonData);
    const nonAvailability = dealCards?.some((dealCard) => dealCard.product?.dates?.soldOut) ? 1 : 0;
    const partialStayData = createPartialStayData(searchParams);

    const data = {
      event: 'visit',
      page: {
        subCategory: undefined,
        category,
        section,
        type: 'bkFlow',
        ...commonPageParams,
      },
      product: {
        accomodation: {
          class: undefined,
          destinationCode: undefined,
          destinationName: undefined,
          destinationCountryCode: undefined,
          destinationCountryName: undefined,
          duration: undefined,
          endDate: undefined,
          internalId: undefined,
          isTopSecret: undefined,
          mealPlan: undefined,
          name: undefined,
          providerId: undefined,
          providerName: undefined,
          quantity: undefined,
          rating: undefined,
          reviewsNumber: undefined,
          startDate: undefined,
          type: undefined,
          withPhoto: undefined,
        },
        adults: undefined,
        advancedBooking: undefined,
        children: undefined,
        infants: undefined,
        name: undefined,
        numberOfGuests: undefined,
        offerType: undefined,
        priceEUR: undefined,
        priceUserCurrency: undefined,
        pricingId: undefined,
        sku: undefined,
        category: productCategory,
      },
      results: {
        idRequest: undefined,
        totalNumberOfResults,
        nonAvailability,
        ...countOffers(dealCards || []),
      },
      search: {
        type: getSource(searchParams[URLParameters.SOURCE]),
        departureDate,
        ...destinationData,
        ...(origin ? originData : {}),
        duration,
        quantity,
        numberOfGuests,
        adults: totalNumAdults,
        children: totalNumChildren,
        infants: totalNumInfants,
        returnDate,
        hdpSearchId: searchId,
        ...partialStayData,
      },
      user: {
        currency,
        deviceType,
        sessionId,
        uid: userUid,
        isBot,
        ...userDqData,
      },
      transactionAffiliation: businessProfile,
      error: undefined,
    };

    logDevOnly({ data, title: 'ANALYTICS RESULTS EVENT' });

    registerEvents(data);
  }
};
